import {createSelector} from '@ngrx/store';
import {AppState} from '../app.state';
import {selectEntities as selectReactionEntitiesBase} from '../reaction/reaction.reducer';
import {createReactionListPageKey, ReactionListPageState, ReactionListsState} from './reaction-list.models';
import {SocialListType} from '../../shared/modal/components/social-list/social-list-type.type';

export const selectReactionListsState = (state: AppState): ReactionListsState => state.reactionLists;
export const selectReactionState = (state: AppState) => state.reactions;

export const selectReactionEntities = createSelector(
  selectReactionState,
  selectReactionEntitiesBase
);

export const selectReactionListPage = (listType: SocialListType, subjectId: string, page: number) => createSelector(
  selectReactionListsState,
  (state: ReactionListsState): ReactionListPageState | null => state.pages[createReactionListPageKey({listType, subjectId, page})] || null
);

export const selectReactionListPagesForSubject = (listType: SocialListType, subjectId: string) => createSelector(
  selectReactionListsState,
  (state: ReactionListsState): Record<number, ReactionListPageState> => {
    const prefix = `${listType}::${subjectId}::`;
    return Object.entries(state.pages).reduce((pages, [key, pageState]) => {
      if (!key.startsWith(prefix)) {
        return pages;
      }

      const page = Number(key.split('::').pop());
      if (Number.isFinite(page)) {
        pages[page] = pageState;
      }
      return pages;
    }, {} as Record<number, ReactionListPageState>);
  }
);

export const selectCombinedReactionList = (listType: SocialListType, subjectId: string) => createSelector(
  selectReactionListPagesForSubject(listType, subjectId),
  selectReactionEntities,
  (pages, entities) => {
    const combinedIds: string[] = [];
    let hasMore = false;
    let loadedPageCount = 0;

    for (let page = 1; pages[page]; page += 1) {
      loadedPageCount += 1;
      combinedIds.push(...pages[page].ids);
      hasMore = pages[page].hasMore;
    }

    return {
      ids: combinedIds,
      reactions: combinedIds.map((id) => entities[id]).filter(Boolean),
      hasMore,
      loadedPageCount
    };
  }
);

export const selectReactionListStatus = (listType: SocialListType, subjectId: string) => createSelector(
  selectReactionListPagesForSubject(listType, subjectId),
  (pages) => {
    return Object.values(pages).some((page) => page.status === 'loading') ? 'loading' : 'idle';
  }
);

